import { CSSProperties, FC, memo, useCallback, useEffect, useRef, useState } from 'react';
import cx, { clsx } from 'clsx';
import { CloverPaymentInput } from 'routes/Checkout/PaymentInfo/CloverPaymentInput';
import styles from './style.module.scss';
import { useCloverPaymentIframeContext } from 'hooks/context/cloverPaymentIframe';
import { Button, ButtonVariant } from '@clover/web-lib-consumer-ui';
import { GIFT_CARD_TESTIDS } from '../const';
import { useTranslation } from 'react-i18next';
import { Icon } from 'components/Icon';
import { useToggle } from '@clover-util/use-toggle';
import { useMerchant } from 'hooks/context/merchant';
import { ToolTipBox } from 'components/ToolTipBox/ToolTipBox';
import { useGiftCard } from 'routes/Checkout/context/GiftCard/GiftCardContext';
import { GiftCardList } from '../GiftCardList/GiftCardList';
import { FieldError } from 'components/FieldError';
import { useInvoiceSummary } from 'routes/Checkout/context/InvoiceSummary';
import { parseErrorMessage, parseErrorType } from 'routes/Checkout/context/SubmitOrder/utils';
import { useOrderTotals } from 'routes/Checkout/context/useOrderTotals';
import { useGiftCardCart } from 'hooks/context/giftCardCart';

interface GiftCardProps {
  cloverElementsInstance: Clover.ElementRoot | undefined;
}

const STYLES: Record<string, CSSProperties| Record<string ,CSSProperties> > = {
  'slot-fb section': {
    display: 'grid',
    gridTemplateColumns: '63% 33%',
  },
  '#giftCardNumber, #giftCardSCV': {
    backgroundColor: '#ffffff',
    height:'52px'
  },
  '#giftCardSCV': {
    margin: '0 0 0 8px',
  },
  '.hydrated': {
    display: 'block',
    height: '7rem',
    overflow: 'hidden',
  },
  '.fieldError input': {
    borderColor: '#e70000',
  },
  '.errorMessage': {
    color: '#e70000',
    fontSize: '.9375rem',
    margin: '4px 0px',
    position: 'absolute',
    left: '0px',
    fontFamily: 'Graphik,Helvetica,sans-serif'
  },
  'input': {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    // TODO: Change to colo standard font-size as part of COLO-1115
    fontSize: '16px',
    height: '1.6em',
    border: '1px solid #727272',
    borderRadius: '4px',
    padding: '24px 12px',
  },
  '#giftCardSCV + .errorMessage': {
    top: '100px',
    fontSize: '.9rem',
  },

  'img': {
    height: '1.2em !important',
    width: '1.8em',
  },
};

export const GiftCardPay: FC<GiftCardProps> = memo(({ cloverElementsInstance }) => {
  const { isGiftcardCheckOutEnabled } = useMerchant();
  const { cloverInstance } = useCloverPaymentIframeContext();
  const [openGiftCard, toggleGiftCard, openGiftCardApply, closeGiftCardApply] = useToggle(false);
  const [openError, toggleError] = useToggle(false);
  const { totalWithTip
  } = useOrderTotals();
  const { value: giftCardElemtValidation, setTrue: validGiftCardElem, setFalse: inValidGiftCardElem } = useToggle(false);
  const [isToolTipVisible, setToolTipVisible] = useState(false);
  const { value: applyButtonStatus, setTrue: enableApplyButton, setFalse: disableApplybutton } = useToggle(true);
  const { clearGiftCard, setGiftCard, removeLastGiftCard } = useGiftCard();
  const { value: giftCardError, setFalse: removeGiftCardError, setTrue: setGiftCardError } = useToggle(false);
  const { value: applyClicked, setTrue: setApplyClicked, setFalse: setApplyClickedFalse } = useToggle(false);
  const [apiErrorType, setApiErrorType] = useState<string | null>(null);
  const [apiErrorMsg, setApiErrorMsg] = useState<string | null>(null);
  const { invoiceSummary, error: invoiceSummaryError } = useInvoiceSummary();
  const [maxGiftCardLimit, setMaxGiftCardLimit] = useState(false);
  const { t } = useTranslation();
  const [prevAppliedGiftCardLength, setPrevAppliedGiftCardLength] = useState(0);
  const { items: giftCardsInCart } = useGiftCardCart();
  const allowGiftCard = giftCardsInCart.length === 0;
  const [disableGiftCard, setDisableGiftCard] = useState(false);
  const [giftCardValidationError, setGiftCardValidationError] = useState(true);
  const [noOfAppliedGC, setNoOfAppliedGC] = useState(0);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (invoiceSummaryError && applyClicked) {
      removeLastGiftCard();
      setApplyClickedFalse();
      const apiErrorCode = parseErrorType(invoiceSummaryError);
      setApiErrorType(apiErrorCode);
      const apiError = parseErrorMessage(invoiceSummaryError);
      setApiErrorMsg(apiError);
      openGiftCardApply();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceSummaryError, applyClicked, removeLastGiftCard, setApplyClickedFalse]);

  useEffect(() => {
    return () => {
      clearGiftCard();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // this is for just setting focus
  useEffect(() => {
    const appliedGiftCardLength = invoiceSummary?.giftSources?.length || 0;
    if (appliedGiftCardLength > 0 && !openGiftCard){
      buttonRef.current?.focus();
    }
  },[invoiceSummary,openGiftCard]);

  // To keep giftcard pannel open only if error thrown,
  useEffect(() => {
    const appliedGiftCardLength = invoiceSummary?.giftSources?.length || 0;
    setNoOfAppliedGC(appliedGiftCardLength);
    if (invoiceSummary && invoiceSummary.giftSources && openGiftCard) {
      if (prevAppliedGiftCardLength !== appliedGiftCardLength) {
        closeGiftCardApply();
        setPrevAppliedGiftCardLength(appliedGiftCardLength);
        if (prevAppliedGiftCardLength > appliedGiftCardLength) {
          setApiErrorMsg(null);
        }
      }
      else if ((prevAppliedGiftCardLength === appliedGiftCardLength)) {
        openGiftCardApply();
      }
    } else if (!invoiceSummaryError && prevAppliedGiftCardLength === 1) {
      setPrevAppliedGiftCardLength(0);
    }
    if (appliedGiftCardLength >= 5) {
      setMaxGiftCardLimit(true);
    } else {
      setMaxGiftCardLimit(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceSummary]);

  const handleValidationChange = useCallback((fieldName: Clover.InputFieldType, state: Clover.ReturnedState) => {
    const { GIFT_CARD_ELEMENTS: giftCardElements } = state;
    const errorState = !giftCardElements.isFormValid;
    removeGiftCardError();
    setApiErrorMsg(null);
    setApiErrorType(null);
    if (errorState) {
      inValidGiftCardElem();
      setGiftCardValidationError(false);
    } else {
      validGiftCardElem();
      setGiftCardValidationError(true);
    }
  }, [inValidGiftCardElem, removeGiftCardError, validGiftCardElem]);

  const handleValidationOnBlur = useCallback((state: Clover.ReturnedState) => {
    const { GIFT_CARD_ELEMENTS: giftCardElements } = state;
    const errorState = !giftCardElements.isFormValid;
    setApiErrorType(null);
    setApiErrorMsg(null);
    enableApplyButton();
    if (errorState) {
      inValidGiftCardElem();
      setGiftCardValidationError(false);
    } else {
      validGiftCardElem();
      setGiftCardValidationError(true);
    }
  }, [enableApplyButton, inValidGiftCardElem, validGiftCardElem]);

  const applyGiftCard = async () => {
    try {
      setApiErrorType(null);
      setApplyClicked();
      disableApplybutton();
      removeGiftCardError();
      let applyButtonEnabled = false;
      const timeout = setTimeout(() => {
        if (!applyButtonEnabled && !apiErrorMsg) {
          setGiftCardError();
          enableApplyButton();
        }
      }, 5000); // 5 seconds
      const { giftcard: { last4 }, token } = await cloverInstance?.createGiftCardToken();
      applyButtonEnabled = true;
      enableApplyButton();
      setGiftCard({
        token,
        last4
      });

      return () => {
        clearTimeout(timeout);
      };
    } catch (error) {
      const apiError = parseErrorMessage(error);
      setApiErrorMsg(apiError);
      const apiErrorType = parseErrorType(invoiceSummaryError);
      setApiErrorType(apiErrorType);
      enableApplyButton();
    }
  };

  useEffect(() => {
    if (allowGiftCard) {
      setDisableGiftCard(true);
    }
  }, [allowGiftCard]);

  const toggleGiftCardSection = () => {
    setApiErrorType(null);
    setApiErrorMsg(null);
    !disableGiftCard ? toggleError() : toggleGiftCard();
    removeGiftCardError();
    setGiftCardValidationError(true);
    disableApplybutton();
  };

  const showToolTip = (e: any) => {
    setToolTipVisible(true);
  };

  const hideToolTip = (e: any) => {
    setToolTipVisible(false);
  };
  if (!isGiftcardCheckOutEnabled) return (null);

  return (
    <>
      <div className={cx(styles.giftWrapper, noOfAppliedGC <= 0 ? styles.giftWrapperwithBg : styles.giftWrapperwithoutBg)}>
        {!maxGiftCardLimit && cloverElementsInstance && totalWithTip > 0 && (
          <>
            {!openGiftCard && noOfAppliedGC > 0 &&
              <Button
                variant={ButtonVariant.TEXT}
                className={cx(styles.customButton, styles.giftCardButton, !disableGiftCard ? styles.disableGiftCard : '')}
                onClick={toggleGiftCardSection}
                data-testid={GIFT_CARD_TESTIDS.addGiftCard}
                role="radio"
                type="button"
                aria-checked="false"
                ref={buttonRef}
              >
                {t('checkoutPage.checkoutGiftCard.addAnotherGiftCard')}
              </Button>
            }
            {noOfAppliedGC <= 0 &&
              <div data-testid={GIFT_CARD_TESTIDS.addGiftCard} className={cx(styles.giftcardAccordianButton)} onClick={toggleGiftCardSection} role="button" tabIndex={0} aria-label="toogleGiftCard"
                onKeyDown={(event) => {
                  if (event.keyCode === 13) {
                    toggleGiftCardSection();
                  }
                }} >
                <div className={!openGiftCard ? cx(styles.closedAccordian) : cx(styles.opendAccordian)}>
                  <p>{t('checkoutPage.checkoutGiftCard.addGiftCard')}</p>
                </div>
              </div>
            }
            <div className={cx(styles.giftCardContainer , openGiftCard ? styles.giftCardContainerOpen : styles.giftCardContainerClose) } >
              {openGiftCard && (
                <>
                <div className={styles.mobGC} >
                  <CloverPaymentInput
                    cloverElementsInstance={cloverElementsInstance}
                    fieldName="GIFT_CARD_ELEMENTS"
                    onValidationChange={handleValidationChange}
                    onValidationBlur={handleValidationOnBlur}
                    className={(!giftCardValidationError) ? cx(styles.giftCardWithError) : cx(styles.giftCard)}
                    style={STYLES}
                  />
                  <div className={styles.applyContainer}>
                    <div
                      className={styles.toolTipIcon}
                      data-testid="toolTipIcon"
                      onMouseEnter={showToolTip}
                      onMouseLeave={hideToolTip}
                      onFocus={showToolTip}
                      onBlur={hideToolTip}
                      role="button"
                      aria-label="toolTipIcon"
                      tabIndex={0}
                    >
                      <Icon name="toolTip" />
                      {isToolTipVisible && (
                        <div className={styles.toolTipContainer} data-testid="toolTipInfo">
                          <ToolTipBox />
                        </div>
                      )}
                    </div>
                  </div>
                  </div>
                  <Button
                    type="submit"
                    className={clsx('p', styles.submit, styles.applyGiftCardButton)}
                    onClick={applyGiftCard}
                    variant={ButtonVariant.PRIMARY}
                    disabled={!applyButtonStatus || !giftCardElemtValidation || !allowGiftCard}
                    data-testid={GIFT_CARD_TESTIDS.applyGiftCard}
                  >
                    {t('checkoutPage.checkoutGiftCard.giftCardApply')}
                  </Button>
                </>
              )}
            </div>
          </>
        )}
        {(giftCardError || apiErrorMsg) && (noOfAppliedGC > 0) && openGiftCard && (
          <div className={styles.fieldError}>
            <FieldError data-testid={GIFT_CARD_TESTIDS.error} className={styles.error} id={GIFT_CARD_TESTIDS.error}>
              {apiErrorType?.toString() === '400' ? apiErrorMsg : t('checkoutPage.errorMessages.genericError')}
            </FieldError>
          </div>
        )}
        <div className={styles.giftCardList}>
          <GiftCardList />
        </div>
      </div>
      {openError && (
        <div
          aria-live="polite"
          role="alert"
          data-testid="disableError"
          className={cx(styles.giftCardError, {
            [styles.giftCardErrorHide]: allowGiftCard,
          })}
        >
          {
            <p>
              <Icon className={styles.icon} name="error" />
              {t('checkoutPage.checkoutGiftCard.giftCardErrorMsg')}
            </p>
          }
        </div>
      )}
      {(giftCardError || apiErrorMsg) && (noOfAppliedGC <= 0) && openGiftCard && (
        <div className={styles.fieldError}>
          <FieldError data-testid={GIFT_CARD_TESTIDS.error} className={styles.error} id={GIFT_CARD_TESTIDS.error}>
            {apiErrorType?.toString() === '400' ? apiErrorMsg : t('checkoutPage.errorMessages.genericError')}
          </FieldError>
        </div>
      )}
    </>
  );
});
